import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionBar, Button} from '@primer/react'
import type {ComponentPropsWithRef} from 'react'

import styles from './ListFilterButton.module.css'

type PrimerButtonProps = ComponentPropsWithRef<typeof Button>
type OverlayTriggerProps = Required<Pick<PrimerButtonProps, 'aria-expanded' | 'aria-haspopup'>>

export type ListFilterButtonProps = Omit<
  PrimerButtonProps,
  'aria-expanded' | 'aria-haspopup' | 'as' | 'className' | 'variant' | 'trailingAction' | 'trailingVisual'
> &
  OverlayTriggerProps & {
    /**
     * Render as a registered item when the button is a direct child of a Primer ActionBar.
     *
     * Popup-backed items that can overflow require a containing adapter to retarget the popup anchor and preserve
     * popup semantics on Primer's generated overflow item.
     */
    isActionBarItem?: boolean
  }

export function ListFilterButton({ref, isActionBarItem = false, ...props}: ListFilterButtonProps) {
  if (isActionBarItem) {
    const disabled = props.disabled || props.loading

    return (
      <ActionBar.Button
        {...props}
        disabled={disabled}
        ref={ref}
        onKeyDown={disabled ? undefined : props.onKeyDown}
        className={styles.ListFilterButton}
        variant="invisible"
        trailingAction={TriangleDownIcon}
      />
    )
  }

  return (
    <Button
      {...props}
      ref={ref}
      className={styles.ListFilterButton}
      variant="invisible"
      trailingAction={TriangleDownIcon}
    />
  )
}

ListFilterButton.displayName = 'ListFilterButton'
